// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// Additional terms under AGPL-3.0 section 7(b) apply; see ADDITIONAL-TERMS.md.
/**
 * runner.ts — one live round: the loop, the HUD, the juice, the opponents.
 *
 * Owns nothing authoritative. In multiplayer every peer runs its OWN tower from
 * the same frozen (seed, mode, roster), so this file never receives tower state
 * — only gusts in, and status/gusts out. That is what makes the netcode
 * desync-proof and host transfer a display concern.
 */

import type { Sfx } from '@ben-gy/game-engine/sound';
import { Game, PLAY_W } from './game';
import { BOT_PRESETS, botPlay, type PolicyId } from './bot';
import { createRenderer, type Renderer } from './render';
import { attachAim, attachKeys, type InputLayer } from './controls';
import { el, setHidden } from './dom';
import * as P from './palette';
import type { Mode } from './modes';
import { makeRng } from '@ben-gy/game-engine/rng';

export interface PlayerResult {
  id: string;
  name: string;
  score: number;
  heightCm: number;
  heightCells: number;
  drops: number;
  settled: number;
  lost: number;
  won: boolean;
  isSelf: boolean;
  isBot: boolean;
  left: boolean;
}

export interface OpponentSpec {
  id: string;
  name: string;
  bot?: PolicyId;
}

export interface RunnerOpts {
  stage: HTMLElement;
  mode: Mode;
  seed: number;
  selfName: string;
  selfId: string;
  reducedMotion: boolean;
  sfx: Sfx;
  opponents: OpponentSpec[];
  onAttack?: () => void;
  onStatus?: (st: { h: number; placed: number; lost: number; alive: boolean }) => void;
  onSelfEnd?: (r: PlayerResult) => void;
  onRoundEnd: (results: PlayerResult[]) => void;
  /** Leave the round in progress and go back to the menu (from the pause card). */
  onQuit?: () => void;
}

interface Well {
  id: string;
  name: string;
  game: Game | null;      // present for self + local bots; null for remote peers
  bot: PolicyId | null;
  isSelf: boolean;
  left: boolean;
  // remote-reported (or mirror of local game)
  h: number;
  placed: number;
  lost: number;
  alive: boolean;
  botAcc: number;
  pendingWind: number;    // a gust to apply on this bot's next placement
  botRng: () => number;
  canvas?: HTMLCanvasElement;
  nameEl?: HTMLElement;
  statEl?: HTMLElement;
}

export interface Runner {
  destroy(): void;
  pause(): void;
  resume(): void;
  togglePause(): void;
  paused(): boolean;
  receiveAttack(n: number): void;
  applyStatus(id: string, st: { h: number; placed: number; lost: number; alive: boolean }): void;
  markLeft(id: string): void;
  finishNow(): void;
  results(): PlayerResult[];
  stepForTest(dtMs: number): void;
  debugGame(): Game;
}

const BOT_STEP_MS = 900;

export function createRunner(opts: RunnerOpts): Runner {
  const { stage, mode, seed, sfx } = opts;

  // ------------------------------------------------------------------ DOM ---
  stage.innerHTML = '';
  const wrap = el('div', 'play');
  const boardBox = el('div', 'board-box');
  const canvas = el('canvas', 'board-canvas');
  canvas.setAttribute('aria-label', 'The tower');
  boardBox.appendChild(canvas);

  const hud = el('div', 'hud');
  const hudTop = el('div', 'hud-top');
  const heightEl = el('div', 'hud-stat', '<span class="hud-k">Height</span><span class="hud-v" id="v-height">0</span>');
  const modeStatEl = el('div', 'hud-stat', `<span class="hud-k" id="v-modek">Goal</span><span class="hud-v" id="v-modev">–</span>`);
  hudTop.append(heightEl, modeStatEl);

  const oppRail = el('div', 'opp-rail');
  setHidden(oppRail, opts.opponents.length === 0);
  hud.append(hudTop, oppRail);

  // control bar: nudge ◀ ▶ (precise aim) + rotate + drop (+ gust vs opponents)
  const controls = el('div', 'controls');
  const nudgeLBtn = el('button', 'ctl-btn nudge');
  nudgeLBtn.type = 'button';
  nudgeLBtn.innerHTML = '<span>◀</span>Nudge';
  nudgeLBtn.setAttribute('aria-label', 'Nudge brick left');
  const nudgeRBtn = el('button', 'ctl-btn nudge');
  nudgeRBtn.type = 'button';
  nudgeRBtn.innerHTML = '<span>▶</span>Nudge';
  nudgeRBtn.setAttribute('aria-label', 'Nudge brick right');
  const rotateBtn = el('button', 'ctl-btn');
  rotateBtn.type = 'button';
  rotateBtn.innerHTML = '<span>↻</span>Rotate';
  const dropBtn = el('button', 'ctl-btn drop');
  dropBtn.type = 'button';
  dropBtn.innerHTML = '<span>▼</span>Drop';
  const gustBtn = el('button', 'ctl-btn gust');
  gustBtn.type = 'button';
  gustBtn.innerHTML = '<span>💨</span>Gust <b id="v-charges">0</b>';
  controls.append(nudgeLBtn, nudgeRBtn, rotateBtn, dropBtn);
  const hasOpponents = opts.opponents.length > 0;
  if (hasOpponents) controls.appendChild(gustBtn);

  const pauseBtn = el('button', 'icon-btn pausebtn');
  pauseBtn.type = 'button';
  pauseBtn.textContent = 'Pause';
  pauseBtn.setAttribute('aria-label', 'Pause');
  boardBox.appendChild(pauseBtn);

  const overlay = el('div', 'game-overlay');
  overlay.setAttribute('hidden', '');
  overlay.innerHTML = '<div class="go-card"><h2 class="go-title">Paused</h2></div>';
  const goCard = overlay.querySelector('.go-card')!;
  const resumeBtn = el('button', 'btn primary');
  resumeBtn.type = 'button';
  resumeBtn.textContent = 'Resume';
  const quitBtn = el('button', 'btn quit');
  quitBtn.type = 'button';
  quitBtn.textContent = 'Quit to menu';
  goCard.append(resumeBtn, quitBtn);
  boardBox.appendChild(overlay);

  const toast = el('div', 'toast');
  boardBox.appendChild(toast);

  wrap.append(boardBox, hud, controls);
  stage.appendChild(wrap);

  const vHeight = wrap.querySelector('#v-height') as HTMLElement;
  const vModeK = wrap.querySelector('#v-modek') as HTMLElement;
  const vModeV = wrap.querySelector('#v-modev') as HTMLElement;
  const vCharges = wrap.querySelector('#v-charges') as HTMLElement | null;

  // ----------------------------------------------------------------- state ---
  const renderer: Renderer = createRenderer(canvas);
  renderer.setReducedMotion(opts.reducedMotion);

  const game = new Game({ mode, seed });
  const self: Well = {
    id: opts.selfId, name: opts.selfName, game, bot: null, isSelf: true, left: false,
    h: 0, placed: 0, lost: 0, alive: true, botAcc: 0, pendingWind: 0, botRng: Math.random,
  };
  const wells: Well[] = [self];
  opts.opponents.forEach((o, i) => {
    wells.push({
      id: o.id, name: o.name,
      game: o.bot ? new Game({ mode, seed }) : null,
      bot: o.bot ?? null,
      isSelf: false, left: false,
      h: 0, placed: 0, lost: 0, alive: true, botAcc: (i + 1) * 140, pendingWind: 0,
      botRng: makeRng((seed ^ 0x51ed ^ (i * 2246822519)) >>> 0),
    });
  });

  for (const w of wells) {
    if (w.isSelf) continue;
    const card = el('div', 'opp');
    const c = el('canvas', 'opp-canvas');
    c.width = 58; c.height = 92;
    const nm = el('div', 'opp-name'); nm.textContent = w.name;
    const st = el('div', 'opp-stat'); st.textContent = '0';
    card.append(c, nm, st);
    oppRail.appendChild(card);
    w.canvas = c; w.nameEl = nm; w.statEl = st;
  }

  let paused = false;
  let ended = false;
  let raf = 0;
  let last = 0;
  const nowMs = () => (typeof performance !== 'undefined' ? performance.now() : Date.now());
  let lastTickAt = nowMs();
  let statusAcc = 0;
  let slowmoUntil = 0;
  let comboToast: { text: string; life: number } | null = null;

  document.body.classList.add('playing');

  // ------------------------------------------------------------------ input ---
  const aimLayer: InputLayer = attachAim(boardBox, {
    toWorldX: (clientX, rectLeft) => renderer.screenToWorldX(clientX, rectLeft),
    onAim: (x) => { if (!paused) game.aimTo(x); },
    onTap: () => { if (!paused) game.rotate(1); },
    enabled: () => !paused && !game.over,
  });
  const keyLayer: InputLayer = attachKeys(window, {
    onMove: (d) => { if (!paused) game.nudge(d); },
    onRotate: (d) => { if (!paused) game.rotate(d); },
    onDrop: () => { if (!paused) game.drop(); },
    onPause: () => togglePause(),
    onGust: () => doGust(),
  });
  nudgeLBtn.addEventListener('click', () => { if (!paused) game.nudgeStep(-1); });
  nudgeRBtn.addEventListener('click', () => { if (!paused) game.nudgeStep(1); });
  rotateBtn.addEventListener('click', () => { if (!paused) game.rotate(1); });
  dropBtn.addEventListener('click', () => { if (!paused) game.drop(); });
  gustBtn.addEventListener('click', () => doGust());
  pauseBtn.addEventListener('click', () => togglePause());
  resumeBtn.addEventListener('click', () => togglePause());
  quitBtn.addEventListener('click', () => opts.onQuit?.());

  function doGust(): void {
    if (paused || game.over) return;
    game.gust(); // emits 'gust' + 'attack'; consumeEvents routes it
  }

  // ------------------------------------------------------------- rendering ---
  function drawMini(w: Well): void {
    const c = w.canvas;
    if (!c) return;
    const g = c.getContext('2d');
    if (!g) return;
    g.clearRect(0, 0, c.width, c.height);
    g.fillStyle = P.SKY_BOTTOM;
    g.fillRect(0, 0, c.width, c.height);
    // plinth
    g.fillStyle = P.PLINTH;
    g.fillRect(c.width / 2 - 8, c.height - 10, 16, 8);
    const h = w.game ? w.game.heightCells : w.h;
    const goal = mode.goalCells || 9;
    const frac = Math.max(0, Math.min(1, h / Math.max(goal, 6)));
    const barH = frac * (c.height - 16);
    g.fillStyle = w.alive && !w.left ? P.brickColor(1) : P.MUTED;
    g.globalAlpha = w.left ? 0.4 : 0.95;
    g.fillRect(c.width / 2 - 10, c.height - 10 - barH, 20, barH);
    g.globalAlpha = 1;
    if (mode.goalCells) {
      const gy = c.height - 10 - (c.height - 16);
      g.strokeStyle = P.GOAL; g.setLineDash([3, 3]); g.lineWidth = 1;
      g.beginPath(); g.moveTo(4, gy); g.lineTo(c.width - 4, gy); g.stroke(); g.setLineDash([]);
    }
    if (w.statEl) w.statEl.textContent = `${(w.game ? w.game.heightCm : Math.round(w.h * 20))}`;
    if (w.nameEl) w.nameEl.classList.toggle('is-left', w.left);
  }

  function paintHud(): void {
    vHeight.textContent = `${game.heightCm}`;
    if (mode.end === 'goal') {
      vModeK.textContent = 'To goal';
      const remain = Math.max(0, mode.goalCells - game.heightCells);
      vModeV.textContent = game.won ? 'REACHED' : `${(remain * 20).toFixed(0)}`;
    } else if (mode.end === 'budget') {
      vModeK.textContent = 'Bricks left';
      vModeV.textContent = String(Math.max(0, mode.budget - game.drops));
    } else {
      vModeK.textContent = 'Peak';
      vModeV.textContent = `${game.heightCm}`;
    }
    if (vCharges) {
      vCharges.textContent = String(game.charges);
      gustBtn.classList.toggle('ready', game.charges > 0);
      gustBtn.disabled = game.charges <= 0;
    }
  }

  // ------------------------------------------------------------------ juice ---
  function consumeEvents(g: Game, w: Well): void {
    for (const e of g.drainEvents()) {
      const isSelf = w.isSelf;
      switch (e.t) {
        case 'spawn': if (isSelf) sfx.play('blip'); break;
        case 'land':
          if (isSelf) {
            renderer.burst(e.x, e.y, P.PLINTH, 6 + Math.min(10, Math.round(e.speed * 3)), 50);
            renderer.shake(1.5 + Math.min(4, e.speed));
            sfx.play('hit');
          }
          break;
        case 'lost':
          if (isSelf) {
            renderer.burst(e.x, e.y, e.wasSettled ? P.KILL : P.MUTED, 10, 90);
            renderer.shake(e.wasSettled ? 6 : 2);
            sfx.play(e.wasSettled ? 'explosion' : 'lose');
            if (e.wasSettled) comboToast = { text: 'A brick slipped off!', life: 900 };
          }
          break;
        case 'rotate': if (isSelf) sfx.play('select'); break;
        case 'charge':
          if (isSelf) { sfx.play('powerup'); comboToast = { text: 'Gust ready — hit the leader', life: 1100 }; }
          break;
        case 'gust': if (isSelf) sfx.play('select'); break;
        case 'attack': routeAttack(w.id); break;
        case 'wind':
          if (isSelf) { renderer.windStreak(1); renderer.shake(2); sfx.play('hit'); comboToast = { text: 'Crosswind!', life: 800 }; }
          break;
        case 'near':
          if (isSelf && !opts.reducedMotion) slowmoUntil = nowMs() + 240;
          break;
        case 'win':
          if (isSelf) { sfx.play('powerup'); renderer.shake(6); comboToast = { text: 'You reached the line!', life: 1400 }; }
          break;
        case 'over': if (isSelf) sfx.play('lose'); break;
        default: break;
      }
    }
  }

  /** A well earned a gust — aim it at the current height leader. */
  function routeAttack(fromId: string): void {
    opts.onAttack?.(); // multiplayer: session addresses the leader among peers
    // local: apply to the leading OTHER well
    const live = wells.filter((w) => w.id !== fromId && !w.left && aliveOf(w));
    if (live.length === 0) return;
    let target = live[0];
    for (const w of live) if (heightOf(w) > heightOf(target)) target = w;
    applyWind(target);
  }

  function applyWind(w: Well): void {
    if (w.isSelf) {
      const dir = Math.sign(game.support().centroidX - PLAY_W / 2) || 1;
      game.receiveWind(dir);
    } else if (w.game) {
      w.pendingWind = Math.sign(w.game.support().centroidX - PLAY_W / 2) || 1;
    }
  }

  const aliveOf = (w: Well) => (w.game ? !w.game.over : w.alive);
  const heightOf = (w: Well) => (w.game ? w.game.heightCells : w.h);

  // ------------------------------------------------------------------- loop ---
  function stepBots(dtMs: number): void {
    for (const w of wells) {
      if (w.isSelf || !w.game || !w.bot || w.left) continue;
      if (w.game.over) { w.alive = false; continue; }
      w.botAcc += dtMs;
      while (w.botAcc >= BOT_STEP_MS && !w.game.over) {
        w.botAcc -= BOT_STEP_MS;
        if (w.pendingWind) { w.game.receiveWind(w.pendingWind); w.pendingWind = 0; }
        const beforeCharges = w.game.charges;
        botPlay(w.game, BOT_PRESETS[w.bot], w.botRng, w.game.drops);
        // bots spend a gust when they have one, at the leader
        if (w.game.charges > 0 && w.game.charges >= beforeCharges) w.game.gust();
        consumeEvents(w.game, w);
        w.h = w.game.heightCells; w.placed = w.game.drops; w.lost = w.game.lostCount;
        w.alive = !w.game.over;
      }
    }
  }

  function tick(dt: number): void {
    if (ended) return;
    lastTickAt = nowMs();

    let simDt = dt;
    if (nowMs() < slowmoUntil && !opts.reducedMotion) simDt = dt * 0.35;

    if (!paused && !game.over) {
      game.step(simDt);
      stepBots(dt);
    }
    consumeEvents(game, self);
    self.h = game.heightCells; self.placed = game.drops; self.lost = game.lostCount; self.alive = !game.over;

    const vignette = nowMs() < slowmoUntil ? (slowmoUntil - nowMs()) / 240 : 0;
    renderer.draw(game, simDt, { vignette });
    paintHud();
    for (const w of wells) if (!w.isSelf) drawMini(w);

    if (comboToast) {
      comboToast.life -= dt;
      if (comboToast.life <= 0) { comboToast = null; toast.textContent = ''; toast.classList.remove('show'); }
      else { toast.textContent = comboToast.text; toast.classList.add('show'); }
    }

    statusAcc += dt;
    if (statusAcc >= 250) {
      statusAcc = 0;
      opts.onStatus?.({ h: game.heightCells, placed: game.drops, lost: game.lostCount, alive: !game.over });
    }

    if (game.over && !ended) maybeFinish();
  }

  function frame(now: number): void {
    raf = requestAnimationFrame(frame);
    const dt = Math.min(50, last ? now - last : 16);
    last = now;
    tick(dt);
  }

  const backstop = window.setInterval(() => {
    if (ended || paused || document.hidden) return;
    const gap = nowMs() - lastTickAt;
    if (gap > 400) { last = 0; tick(Math.min(50, gap)); }
  }, 250);

  let selfReported = false;
  function maybeFinish(): void {
    if (!selfReported) {
      selfReported = true;
      self.alive = false;
      opts.onSelfEnd?.(resultOf(self));
    }
    const localOnly = wells.every((w) => w.isSelf || w.game !== null);
    if (localOnly && wells.every((w) => !aliveOf(w) || w.left)) finishNow();
  }

  function resultOf(w: Well): PlayerResult {
    const g = w.game;
    return {
      id: w.id, name: w.name,
      score: g ? g.score : Math.round(w.h * 100),
      heightCm: g ? g.heightCm : Math.round(w.h * 20),
      heightCells: g ? g.maxHeightCells : w.h,
      drops: g ? g.drops : w.placed,
      settled: g ? g.settledCount : 0,
      lost: g ? g.lostCount : w.lost,
      won: g ? g.won : false,
      isSelf: w.isSelf, isBot: w.bot !== null, left: w.left,
    };
  }

  function finishNow(): void {
    if (ended) return;
    ended = true;
    document.body.classList.remove('playing');
    opts.onRoundEnd(wells.map(resultOf));
  }

  function togglePause(): void {
    if (ended || game.over) return;
    paused = !paused;
    setHidden(overlay, !paused);
    if (!paused) last = 0;
  }

  const onVisibility = () => { if (document.hidden && !paused) togglePause(); };
  document.addEventListener('visibilitychange', onVisibility);
  const onResize = () => renderer.resize();
  window.addEventListener('resize', onResize);
  window.addEventListener('orientationchange', onResize);

  renderer.resize();
  paintHud();
  for (const w of wells) if (!w.isSelf) drawMini(w);
  raf = requestAnimationFrame(frame);

  return {
    destroy() {
      ended = true;
      cancelAnimationFrame(raf);
      clearInterval(backstop);
      aimLayer.destroy();
      keyLayer.destroy();
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('resize', onResize);
      window.removeEventListener('orientationchange', onResize);
      document.body.classList.remove('playing');
    },
    pause() { if (!paused) togglePause(); },
    resume() { if (paused) togglePause(); },
    togglePause,
    paused: () => paused,
    receiveAttack() { applyWind(self); },
    applyStatus(id, st) {
      const w = wells.find((x) => x.id === id);
      if (!w || w.isSelf || w.game) return;
      w.h = st.h; w.placed = st.placed; w.lost = st.lost; w.alive = st.alive;
      drawMini(w);
    },
    markLeft(id) {
      const w = wells.find((x) => x.id === id);
      if (!w) return;
      w.left = true; w.alive = false;
      drawMini(w);
      if (game.over) maybeFinish();
    },
    finishNow,
    results: () => wells.map(resultOf),
    stepForTest(dtMs) { tick(dtMs); },
    debugGame: () => game,
  };
}